import type { State } from "@prisma/client";

export interface TimezoneOption {
  /** IANA zone name — the single source of truth stored on User.timezone. */
  value: string;
  label: string;
}

/**
 * The IANA timezones an admin can assign to a user. Values must be IANA zone
 * names (never hand-computed UTC offsets) so downstream date math stays
 * DST-aware. `as const satisfies` keeps the literal `value` strings so
 * `SupportedTimezone` below can be derived as a closed union instead of
 * every zone-carrying signature just taking `string`.
 */
export const US_TIMEZONE_OPTIONS = [
  { value: "America/New_York", label: "Eastern (America/New_York)" },
  { value: "America/Chicago", label: "Central (America/Chicago)" },
  { value: "America/Denver", label: "Mountain (America/Denver)" },
  { value: "America/Phoenix", label: "Arizona (America/Phoenix)" },
  { value: "America/Los_Angeles", label: "Pacific (America/Los_Angeles)" },
  { value: "America/Anchorage", label: "Alaska (America/Anchorage)" },
  { value: "Pacific/Honolulu", label: "Hawaii (Pacific/Honolulu)" },
] as const satisfies readonly TimezoneOption[];

/** The closed set of IANA zones scheduling code is allowed to operate in. */
export type SupportedTimezone = (typeof US_TIMEZONE_OPTIONS)[number]["value"];

/**
 * Marble's operating timezone (Eastern), used until a user's timezone is
 * explicitly set and as the fallback whenever a provider's zone can't be
 * used.
 */
export const DEFAULT_TIMEZONE: SupportedTimezone = "America/New_York";

/**
 * The states where the zone a client reads is genuinely ambiguous, and every
 * zone a client receiving care there may legitimately be in. The first entry
 * is the default and always matches `STATE_TIMEZONES` below.
 *
 * Arizona is the only real case. The state does not observe DST, but the
 * Navajo Nation in its northeast corner does, so a family there is an hour
 * ahead of Phoenix for half the year. States that merely straddle a zone line
 * on the map (Florida's panhandle, west Texas) are deliberately absent: we
 * have never had a client on the minority side, and an entry here is an option
 * an admin can pick wrongly.
 */
export const MULTI_ZONE_STATE_TIMEZONES: Partial<
  Record<State, readonly SupportedTimezone[]>
> = {
  AZ: ["America/Phoenix", "America/Denver"],
};

/**
 * Best-effort US state/territory → supported scheduling zone, used to
 * derive a client's "state of care" timezone (resolveClientTimezone in
 * timezones/queries.ts) from their insurance/home state.
 * States that span more than one real US zone are mapped to whichever zone
 * covers the majority of the state's population; states/territories with no
 * reasonable match among US_TIMEZONE_OPTIONS (military addresses, Pacific
 * territories, Puerto Rico/USVI's own Atlantic zone) are simply omitted —
 * callers fall back to DEFAULT_TIMEZONE for anything missing here.
 */
export const STATE_TIMEZONES: Partial<Record<State, SupportedTimezone>> = {
  AL: "America/Chicago",
  AK: "America/Anchorage",
  AZ: "America/Phoenix",
  AR: "America/Chicago",
  CA: "America/Los_Angeles",
  CO: "America/Denver",
  CT: "America/New_York",
  DC: "America/New_York",
  DE: "America/New_York",
  FL: "America/New_York",
  GA: "America/New_York",
  HI: "Pacific/Honolulu",
  ID: "America/Denver",
  IL: "America/Chicago",
  IN: "America/New_York",
  IA: "America/Chicago",
  KS: "America/Chicago",
  KY: "America/New_York",
  LA: "America/Chicago",
  ME: "America/New_York",
  MD: "America/New_York",
  MA: "America/New_York",
  MI: "America/New_York",
  MN: "America/Chicago",
  MS: "America/Chicago",
  MO: "America/Chicago",
  MT: "America/Denver",
  NE: "America/Chicago",
  NV: "America/Los_Angeles",
  NH: "America/New_York",
  NJ: "America/New_York",
  NM: "America/Denver",
  NY: "America/New_York",
  NC: "America/New_York",
  ND: "America/Chicago",
  OH: "America/New_York",
  OK: "America/Chicago",
  OR: "America/Los_Angeles",
  PA: "America/New_York",
  RI: "America/New_York",
  SC: "America/New_York",
  SD: "America/Chicago",
  TN: "America/Chicago",
  TX: "America/Chicago",
  UT: "America/Denver",
  VT: "America/New_York",
  VA: "America/New_York",
  WA: "America/Los_Angeles",
  WV: "America/New_York",
  WI: "America/Chicago",
  WY: "America/Denver",
};

/**
 * The timezone Marble's business day is measured in. Distinct from a user's
 * own timezone: SLA cutoffs, business-day math and "before 8 AM" rules are all
 * anchored to the company's day, not the client's.
 *
 * In `@marble/core` because both `apps/web` (task and case SLAs) and
 * `workers/lambdas` (crons that create the same tasks) need it.
 */
export const COMPANY_TZ: SupportedTimezone = "America/New_York";

/**
 * The zone to read a **stored calendar date** on — a licence expiry, a
 * Medicaid ID expiry, a DEA expiry. Not a zone anyone is in.
 *
 * These columns are `DateTime` but hold a day, not an instant. UTC midnight
 * is the normal form for that: it is what every write path produces, so UTC
 * is the zone that reads the day back unchanged. Writes from a date picker
 * go through `localCalendarDayAsUtcMidnight`, which keeps the day the admin
 * picked and drops the instant.
 *
 * Reading one of these on somebody's clock instead is an off-by-one, not a
 * courtesy: UTC midnight is the previous evening in every US zone, so an
 * expiry would show a day early for every reader. That is the mistake this
 * constant exists to name — a render of one of these columns should use it
 * rather than pick a zone that looks reasonable.
 *
 * `TherapistLicense` needed a one-off backfill to get here, run out of band
 * in September 2026: the licence renewal modal used to stamp the picked day
 * with end-of-day on the admin's own browser clock, which landed 197 rows on
 * the following UTC day and rendered them a day late.
 */
export const EXPIRY_DATE_TZ = "UTC";

/**
 * Arizona, for the handful of places that deliberately reckon on the Arizona
 * clock rather than the company's — an Arizona-only Slack digest, or a cron
 * window that has to stay open until the last Arizona session ends.
 *
 * Named so those places read as a decision instead of a stray literal. This is
 * not the zone to reach for when you mean "this client's zone" — that comes
 * from `resolveClientTimezoneFromUser`, which returns Phoenix for an Arizona
 * client already.
 */
export const ARIZONA_TZ: SupportedTimezone = "America/Phoenix";
